import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { videos } from "@/mocks/videos";
import type { Video } from "@/types/video";
import { getCategory } from "@/utils/video";
import { usePageMeta } from "@/hooks/usePageMeta";
import VideoCard from "@/components/base/VideoCard";
import FilterBar, { type FilterState } from "./components/FilterBar";
import Pagination from "./components/Pagination";

const PAGE_SIZE = 6;
const defaultFilters: FilterState = { region: "全部", year: "全部", sort: "latest" };

export default function Category() {
  const { type } = useParams();
  const category = getCategory(type);
  const [filters, setFilters] = useState<FilterState>(defaultFilters);
  const [page, setPage] = useState(1);

  const categoryName = category ? category.name : "影视";

  usePageMeta(
    `91视频在线观看 - ${categoryName}影视全集免费在线观看`,
    `91视频在线观看${categoryName}频道，提供最新${categoryName}资源免费在线观看，支持按地区、年份筛选与最新、最热、评分排序，${category?.desc || ""}`,
    `91视频在线观看,${categoryName}在线观看,${categoryName}免费观看,影视资源,高清视频`,
  );

  const list = useMemo(() => {
    const base = (videos as Video[]).filter((v) => (category ? v.typeKey === category.key : true));
    const filtered = base.filter((v) => {
      const regionOk = filters.region === "全部" || v.region === filters.region;
      const yearOk = filters.year === "全部" || `${v.year}` === filters.year;
      return regionOk && yearOk;
    });
    const sorted = [...filtered].sort((a, b) => {
      if (filters.sort === "hot") return b.views - a.views;
      if (filters.sort === "rating") return b.rating - a.rating;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
    return sorted;
  }, [category, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters, type]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const paged = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500">
        <Link to="/" className="transition hover:text-primary-400">首页</Link>
        <i className="ri-arrow-right-s-line"></i>
        <span className="text-foreground-800">{categoryName}</span>
      </nav>

      <header className="mt-4">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          {categoryName}频道
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          影片类型：{categoryName}
          {category ? ` · ${category.desc}` : ""}
        </p>
      </header>

      <div className="mt-6">
        <FilterBar
          filters={filters}
          onChange={setFilters}
          onReset={() => setFilters(defaultFilters)}
          resultCount={list.length}
        />
      </div>

      {paged.length > 0 ? (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {paged.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
          <Pagination page={current} totalPages={totalPages} onChange={setPage} />
        </>
      ) : (
        <div className="mt-16 flex flex-col items-center justify-center rounded-lg border border-background-200/70 bg-background-100 py-20 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
            <i className="ri-film-line text-3xl"></i>
          </div>
          <p className="mt-5 text-base font-medium text-foreground-900">
            暂无符合条件的影视资源
          </p>
          <p className="mt-2 text-sm text-foreground-500">试试调整地区或年份筛选条件</p>
          <button
            type="button"
            onClick={() => setFilters(defaultFilters)}
            className="mt-6 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition hover:bg-primary-600"
          >
            重置筛选
          </button>
        </div>
      )}
    </div>
  );
}